import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  Mail as MailIcon, Inbox, Send, Trash2, FileText, Search, Paperclip,
  Loader2, RefreshCw, Download, ChevronLeft, CheckCheck, Eye, EyeOff,
  Bell, BellOff,
} from 'lucide-react';
import { api, useAuthStore } from '../lib/auth';
import { toast } from 'sonner';
import { normalizeTr } from './planning/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ROLE_META = {
  inbox:  { label: 'Gelen Kutusu',  Icon: Inbox },
  sent:   { label: 'Gönderilmiş',   Icon: Send },
  drafts: { label: 'Taslaklar',     Icon: FileText },
  trash:  { label: 'Çöp Kutusu',    Icon: Trash2 },
  other:  { label: '',              Icon: Inbox },
};
function pickMeta(role, folderName) {
  const m = ROLE_META[role] || ROLE_META.other;
  if (role === 'other' || !m.label) {
    // For non-canonical folders show the leaf name (drop "INBOX." prefix etc.)
    const leaf = (folderName || '').replace(/^INBOX[./]/i, '').replace(/^\[Gmail\]\//i, '') || folderName;
    return { label: leaf, Icon: m.Icon };
  }
  return m;
}

/**
 * Read-only IMAP inbox viewer.
 *
 * Layout:
 *   ┌────────────┬─────────────────────────┬────────────────────┐
 *   │ accounts + │  message list  (search) │   message body     │
 *   │  folders   │                         │   + attachments    │
 *   └────────────┴─────────────────────────┴────────────────────┘
 */
export default function MailViewer() {
  const currentUser = useAuthStore((s) => s.user);
  const isSuperAdmin = currentUser?.role === 'SUPER_ADMIN';
  const [accounts, setAccounts] = useState([]);
  const [accountId, setAccountId] = useState(null);
  // Either a canonical role (inbox/sent/trash/drafts) or a raw folder name
  // (for "other" folders the IMAP server reports). Selection state holds
  // both so the message-list query can pick the right filter.
  const [selFolder, setSelFolder] = useState({ role: 'inbox', folder: null });
  const [folderTree, setFolderTree] = useState({ pinned: [], others: [] });
  const [messages, setMessages] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [selectedMsg, setSelectedMsg] = useState(null);
  const [search, setSearch] = useState('');
  const [syncing, setSyncing] = useState(false);

  // ── Load accounts on mount and pick the first active one ───────────
  useEffect(() => {
    (async () => {
      try {
        const res = await api.get('/mail/accounts');
        const list = res.data?.data || [];
        setAccounts(list);
        if (list.length > 0 && !accountId) setAccountId(list[0].id);
      } catch {
        toast.error('Hesaplar yüklenemedi');
      }
    })();
    // eslint-disable-next-line
  }, []);

  // ── Load folder list whenever account changes ───────────────────────
  const reloadFolders = async (acctId = accountId) => {
    if (!acctId) return;
    try {
      const res = await api.get(`/mail/accounts/${acctId}/folders`);
      setFolderTree(res.data?.data || { pinned: [], others: [] });
    } catch { /* silent */ }
  };

  // Live IDLE-equivalent push: backend broadcasts `mail:new` whenever a
  // sync run finds new messages. Refresh folder badges immediately and
  // show a toast preview so the operator doesn't have to wait for the
  // next 1-min poll. Re-trigger the message list reload too if the new
  // mail landed in the folder we're currently viewing.
  useEffect(() => {
    const onPush = (e) => {
      const d = e.detail || {};
      if (d.account_id && d.account_id !== accountId) return;
      reloadFolders(d.account_id || accountId);
      setSelFolder((s) => ({ ...s }));
      const top = (d.preview && d.preview[0]) || null;
      const sender = top ? (top.from_name || top.from_addr) : '';
      const subj = top?.subject || '';
      toast.success(
        d.new_count > 1 ? `${d.new_count} yeni e-posta` : `Yeni e-posta · ${sender}`,
        { description: subj.slice(0, 90), duration: 5000 },
      );
    };
    window.addEventListener('mail-new', onPush);
    return () => window.removeEventListener('mail-new', onPush);
    // eslint-disable-next-line
  }, [accountId]);
  useEffect(() => {
    setSelFolder({ role: 'inbox', folder: null });
    setSelectedId(null);
    reloadFolders(accountId);
    // eslint-disable-next-line
  }, [accountId]);

  // ── Load message list when account/folder/search changes ────────────
  useEffect(() => {
    if (!accountId) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams({ account_id: accountId, limit: 100 });
        if (selFolder.role && selFolder.role !== 'other') params.set('folder_role', selFolder.role);
        else if (selFolder.folder) params.set('folder', selFolder.folder);
        if (search.trim()) params.set('q', search.trim());
        const res = await api.get(`/mail/messages?${params}`);
        if (cancelled) return;
        setMessages(res.data?.data || []);
        setTotal(res.data?.total || 0);
      } catch {
        if (!cancelled) toast.error('Mesajlar yüklenemedi');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [accountId, selFolder, search]);

  // Open a message — and silently mark it as read for the current user.
  // Per-user state only: other users still see it bold/unread.
  useEffect(() => {
    if (!selectedId) { setSelectedMsg(null); return; }
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get(`/mail/messages/${selectedId}`);
        if (cancelled) return;
        const data = res.data?.data || null;
        setSelectedMsg(data);
        if (data && data.is_unread) {
          // fire-and-forget; refresh list/folders so badges drop immediately
          api.post(`/mail/messages/${selectedId}/read`).then(() => {
            setMessages((prev) => prev.map((m) => m.id === selectedId ? { ...m, is_unread: false } : m));
            reloadFolders();
          }).catch(() => { /* noop */ });
        }
      } catch { if (!cancelled) toast.error('Mesaj açılamadı'); }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedId]);

  // ── Read-state toggles ─────────────────────────────────────────────
  const toggleMyRead = async () => {
    if (!selectedMsg) return;
    const wasUnread = selectedMsg.is_unread;
    const path = wasUnread ? 'read' : 'unread';
    try {
      await api.post(`/mail/messages/${selectedMsg.id}/${path}`);
      setSelectedMsg((m) => m ? { ...m, is_unread: !wasUnread } : m);
      setMessages((prev) => prev.map((m) => m.id === selectedMsg.id ? { ...m, is_unread: !wasUnread } : m));
      reloadFolders();
    } catch {
      toast.error('Durum değiştirilemedi');
    }
  };

  const toggleGlobalRead = async () => {
    if (!selectedMsg) return;
    const wasGlobal = !!selectedMsg.read_globally;
    const path = wasGlobal ? 'unread-globally' : 'read-globally';
    try {
      await api.post(`/mail/messages/${selectedMsg.id}/${path}`);
      setSelectedMsg((m) => m ? { ...m, read_globally: !wasGlobal, is_unread: wasGlobal ? m.is_unread : false } : m);
      setMessages((prev) => prev.map((m) => m.id === selectedMsg.id ? { ...m, read_globally: !wasGlobal, is_unread: wasGlobal ? m.is_unread : false } : m));
      toast.success(wasGlobal ? 'Tüm kullanıcılarda okunmadı' : 'Tüm kullanıcılarda okundu işaretlendi');
      reloadFolders();
    } catch {
      toast.error('İşlem başarısız');
    }
  };

  // Desktop notification permission UI. We never auto-ask — that's a bad
  // UX pattern — but once the user is on the MailViewer page we surface a
  // one-off "Enable desktop notifications" button in the header so they
  // can opt in. Once granted/denied it hides.
  const [notifyPerm, setNotifyPerm] = useState(
    typeof Notification !== 'undefined' ? Notification.permission : 'denied'
  );
  const requestDesktopNotifications = async () => {
    if (typeof Notification === 'undefined') {
      toast.error('Bu tarayıcı bildirimleri desteklemiyor');
      return;
    }
    try {
      const p = await Notification.requestPermission();
      setNotifyPerm(p);
      if (p === 'granted') toast.success('Masaüstü bildirimleri açıldı');
      else if (p === 'denied') toast.error('Bildirim izni reddedildi');
    } catch { /* noop */ }
  };

  const markFolderAllReadForMe = async () => {
    if (!accountId) return;
    const params = new URLSearchParams({ account_id: accountId });
    if (selFolder.role && selFolder.role !== 'other') params.set('folder_role', selFolder.role);
    else if (selFolder.folder) params.set('folder', selFolder.folder);
    try {
      const res = await api.post(`/mail/folders/mark-all-read?${params}`);
      const n = res.data?.data?.marked || 0;
      toast.success(`${n} mesaj okundu işaretlendi (sadece sizin için)`);
      setMessages((prev) => prev.map((m) => ({ ...m, is_unread: false })));
      reloadFolders();
    } catch {
      toast.error('İşlem başarısız');
    }
  };

  const handleSync = async () => {
    if (!accountId || syncing) return;
    setSyncing(true);
    try {
      const res = await api.post(`/mail/accounts/${accountId}/sync`);
      const d = res.data?.data || {};
      toast.success(`Senkronizasyon tamam · yeni: ${d.new ?? 0} · klasörler: ${(d.folders || []).join(', ') || '-'}`);
      await reloadFolders();
      // Re-trigger message list reload by tickling selFolder reference
      setSelFolder((s) => ({ ...s }));
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Senkronizasyon başarısız');
    } finally {
      setSyncing(false);
    }
  };

  const downloadAttachment = async (att) => {
    try {
      const token = useAuthStore.getState().accessToken;
      const res = await axios.get(`${API_URL}/api/mail/attachments/${att.id}/download`, {
        responseType: 'blob',
        headers: { Authorization: `Bearer ${token}` },
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url; a.download = att.filename || 'attachment';
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch { toast.error('Ek indirilemedi'); }
  };

  const account = accounts.find((a) => a.id === accountId);

  // Local Turkish-fold filter on top of server search (so typing finds more)
  const filtered = useMemo(() => {
    if (!search.trim()) return messages;
    const q = normalizeTr(search.trim());
    return messages.filter((m) =>
      normalizeTr(m.subject || '').includes(q) ||
      normalizeTr(m.from_addr || '').includes(q) ||
      normalizeTr(m.snippet || '').includes(q)
    );
  }, [messages, search]);

  return (
    <div
      className="h-[calc(100vh-64px)] bg-slate-50 dark:bg-slate-950 md:grid md:grid-cols-[220px_360px_1fr] flex flex-col md:flex-row"
      data-testid="mail-viewer-page"
    >
      {/* ── Accounts + folders ─────────────────────────── */}
      <aside className={`border-r border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 md:overflow-y-auto ${selectedId ? 'hidden md:block' : 'block'}`}>
        <div className="p-3 border-b border-slate-100 dark:border-slate-800">
          <div className="text-[10px] uppercase tracking-wide text-slate-400 mb-1.5">Hesaplar</div>
          {accounts.length === 0 && <p className="text-xs text-slate-400 italic">Yetkili hesap yok</p>}
          <div className="space-y-1">
            {accounts.map((a) => (
              <button
                key={a.id}
                onClick={() => { setAccountId(a.id); setSelectedId(null); }}
                className={`w-full text-left px-2 py-1.5 rounded text-xs ${a.id === accountId ? 'bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-300 font-semibold' : 'hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-600 dark:text-slate-300'}`}
                data-testid={`mail-account-pick-${a.id}`}
              >
                <div className="font-semibold truncate">{a.label}</div>
                <div className="text-[10px] opacity-70 truncate">{a.email_address}</div>
              </button>
            ))}
          </div>
          {/* Notification opt-in — shows only while permission is unknown
              so granted/denied users don't see a dead button forever. */}
          {notifyPerm === 'default' && (
            <button
              onClick={requestDesktopNotifications}
              className="mt-3 w-full flex items-center justify-center gap-1.5 text-[11px] text-emerald-700 border border-emerald-200 rounded px-2 py-1.5 hover:bg-emerald-50"
              data-testid="mail-notify-enable"
            >
              <Bell className="h-3.5 w-3.5" />
              Masaüstü bildirimlerini aç
            </button>
          )}
          {notifyPerm === 'granted' && (
            <div className="mt-3 flex items-center justify-center gap-1 text-[10px] text-emerald-600">
              <Bell className="h-3 w-3" /> Bildirimler aktif
            </div>
          )}
          {notifyPerm === 'denied' && (
            <div className="mt-3 flex items-center justify-center gap-1 text-[10px] text-slate-400" title="Tarayıcı ayarlarından izin verebilirsiniz">
              <BellOff className="h-3 w-3" /> Bildirimler kapalı
            </div>
          )}
        </div>

        {accountId && (
          <div className="p-3">
            <div className="flex items-center justify-between mb-1.5">
              <div className="text-[10px] uppercase tracking-wide text-slate-400">Klasörler</div>
              <button
                onClick={handleSync}
                disabled={syncing}
                className="text-[10px] text-emerald-600 hover:text-emerald-700 disabled:opacity-50 inline-flex items-center gap-1"
                data-testid="mail-sync-now"
                title="Şimdi senkronize et"
              >
                <RefreshCw className={`h-3 w-3 ${syncing ? 'animate-spin' : ''}`} />
                Senkronize
              </button>
            </div>
            <div className="space-y-0.5">
              {folderTree.pinned.map((f) => {
                const meta = pickMeta(f.role, f.folder);
                const Icon = meta.Icon;
                const active = selFolder.role === f.role;
                return (
                  <button
                    key={f.role}
                    onClick={() => { setSelFolder({ role: f.role, folder: null }); setSelectedId(null); }}
                    className={`w-full flex items-center gap-2 px-2 py-1.5 rounded text-xs ${active ? 'bg-slate-100 dark:bg-slate-800 text-slate-900 dark:text-slate-100 font-semibold' : 'hover:bg-slate-50 dark:hover:bg-slate-800/40 text-slate-600 dark:text-slate-300'}`}
                    data-testid={`mail-folder-role-${f.role}`}
                  >
                    <Icon className="h-3.5 w-3.5" />
                    <span className="flex-1 text-left">{meta.label}</span>
                    {f.unread > 0 && <Badge variant="default" className="text-[9px] bg-emerald-500">{f.unread}</Badge>}
                    {f.count > 0 && <Badge variant="outline" className="text-[9px]">{f.count}</Badge>}
                  </button>
                );
              })}
              {folderTree.others.length > 0 && (
                <div className="pt-2 mt-2 border-t border-slate-100 dark:border-slate-800">
                  <div className="text-[9px] uppercase tracking-wide text-slate-400 px-2 mb-1">Diğer</div>
                  {folderTree.others.map((f) => {
                    const meta = pickMeta('other', f.folder);
                    const Icon = meta.Icon;
                    const active = selFolder.folder === f.folder && selFolder.role === 'other';
                    return (
                      <button
                        key={f.folder}
                        onClick={() => { setSelFolder({ role: 'other', folder: f.folder }); setSelectedId(null); }}
                        className={`w-full flex items-center gap-2 px-2 py-1.5 rounded text-xs ${active ? 'bg-slate-100 dark:bg-slate-800 text-slate-900 dark:text-slate-100 font-semibold' : 'hover:bg-slate-50 dark:hover:bg-slate-800/40 text-slate-600 dark:text-slate-300'}`}
                        data-testid={`mail-folder-${f.folder}`}
                        title={f.folder}
                      >
                        <Icon className="h-3.5 w-3.5" />
                        <span className="flex-1 text-left truncate">{meta.label}</span>
                        {f.count > 0 && <Badge variant="outline" className="text-[9px]">{f.count}</Badge>}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        )}
      </aside>

      {/* ── Message list ───────────────────────────────── */}
      <section className={`border-r border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 flex flex-col ${selectedId ? 'hidden md:flex' : 'flex flex-1'}`}>
        <div className="p-3 border-b border-slate-100 dark:border-slate-800 flex items-center gap-2">
          <h2 className="text-sm font-semibold flex-1 truncate">
            {account?.label} <span className="text-slate-400 font-normal">· {pickMeta(selFolder.role, selFolder.folder).label}</span>
          </h2>
          {accountId && total > 0 && (
            <button
              onClick={markFolderAllReadForMe}
              className="text-[10px] text-slate-500 hover:text-emerald-600 inline-flex items-center gap-1"
              data-testid="mail-mark-folder-read"
              title="Bu klasördeki tüm mesajları kendim için okundu olarak işaretle"
            >
              <CheckCheck className="h-3 w-3" /> Tümü okundu
            </button>
          )}
          <Badge variant="outline" className="text-[10px]">{total} mesaj</Badge>
        </div>
        <div className="p-2 border-b border-slate-100 dark:border-slate-800">
          <div className="relative">
            <Search className="absolute left-2 top-2 h-3.5 w-3.5 text-slate-400" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Konu / gönderen ara..." className="pl-7 h-8 text-xs" data-testid="mail-list-search" />
          </div>
        </div>
        <div className="flex-1 overflow-y-auto" data-testid="mail-list">
          {loading ? (
            <div className="p-8 text-center"><Loader2 className="h-5 w-5 animate-spin text-slate-400 mx-auto" /></div>
          ) : filtered.length === 0 ? (
            <p className="p-8 text-center text-xs text-slate-400 italic">Mesaj yok</p>
          ) : (
            filtered.map((m) => (
              <button
                key={m.id}
                onClick={() => setSelectedId(m.id)}
                className={`w-full text-left px-3 py-2 border-b border-slate-100 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-800/40 ${selectedId === m.id ? 'bg-emerald-50 dark:bg-emerald-900/20' : ''}`}
                data-testid={`mail-list-item-${m.id}`}
              >
                <div className="flex items-center gap-2 mb-0.5">
                  <span className={`text-xs truncate flex-1 ${m.is_unread ? 'font-bold text-slate-900 dark:text-slate-100' : 'text-slate-600 dark:text-slate-300'}`}>
                    {m.from_name || m.from_addr || '(bilinmeyen)'}
                  </span>
                  {m.has_attachments && <Paperclip className="h-3 w-3 text-slate-400 shrink-0" />}
                  <span className="text-[10px] text-slate-400 shrink-0">{m.date ? new Date(m.date).toLocaleDateString('tr-TR') : ''}</span>
                </div>
                <div className={`text-xs truncate ${m.is_unread ? 'font-semibold' : ''}`}>{m.subject}</div>
                <div className="text-[10px] text-slate-500 truncate mt-0.5">{m.snippet}</div>
              </button>
            ))
          )}
        </div>
      </section>

      {/* ── Message body ────────────────────────────────── */}
      <section className={`bg-white dark:bg-slate-900 flex flex-col overflow-hidden ${selectedId ? 'flex flex-1' : 'hidden md:flex'}`}>
        {!selectedMsg ? (
          <div className="flex-1 flex flex-col items-center justify-center text-slate-400 text-sm">
            <MailIcon className="h-10 w-10 mb-2 opacity-30" />
            <p>Bir mesaj seçin</p>
          </div>
        ) : (
          <>
            <div className="p-4 border-b border-slate-100 dark:border-slate-800">
              <div className="flex items-start gap-2">
                <Button size="sm" variant="ghost" className="h-7" onClick={() => setSelectedId(null)} data-testid="mail-back">
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <div className="flex-1 min-w-0">
                  <h3 className="text-base font-semibold leading-tight">{selectedMsg.subject}</h3>
                  <div className="text-xs text-slate-500 mt-1">
                    <b>{selectedMsg.from_name || selectedMsg.from_addr}</b>{' '}
                    <span className="text-slate-400">&lt;{selectedMsg.from_addr}&gt;</span>
                  </div>
                  <div className="text-[11px] text-slate-400 mt-0.5">
                    {selectedMsg.date ? new Date(selectedMsg.date).toLocaleString('tr-TR') : ''} · {selectedMsg.folder}
                    {selectedMsg.read_globally && (
                      <Badge variant="outline" className="ml-2 text-[9px] text-blue-700 border-blue-200">
                        Tüm kullanıcılarda okundu
                      </Badge>
                    )}
                  </div>
                  {selectedMsg.to_addrs?.length > 0 && (
                    <div className="text-[11px] text-slate-500 mt-1">
                      <span className="font-semibold">Kime:</span> {selectedMsg.to_addrs.join(', ')}
                    </div>
                  )}
                </div>
                <div className="flex flex-col gap-1 shrink-0">
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-7 text-[11px]"
                    onClick={toggleMyRead}
                    data-testid="mail-toggle-my-read"
                    title="Sadece sizin için okundu/okunmadı durumunu değiştirir"
                  >
                    {selectedMsg.is_unread ? <Eye className="h-3 w-3 mr-1" /> : <EyeOff className="h-3 w-3 mr-1" />}
                    {selectedMsg.is_unread ? 'Okundu yap' : 'Okunmadı yap'}
                  </Button>
                  {isSuperAdmin && (
                    <Button
                      size="sm"
                      variant={selectedMsg.read_globally ? 'default' : 'outline'}
                      className={`h-7 text-[11px] ${selectedMsg.read_globally ? 'bg-blue-600 hover:bg-blue-700 text-white' : ''}`}
                      onClick={toggleGlobalRead}
                      data-testid="mail-toggle-global-read"
                      title="Bu mesajı her kullanıcı için okundu/okunmadı yap"
                    >
                      <CheckCheck className="h-3 w-3 mr-1" />
                      {selectedMsg.read_globally ? 'Tüm kull. iptal' : 'Tüm kullanıcılarda okundu'}
                    </Button>
                  )}
                </div>
              </div>
              {selectedMsg.attachments?.length > 0 && (
                <div className="mt-3 flex flex-wrap gap-2">
                  {selectedMsg.attachments.map((att) => (
                    <button
                      key={att.id}
                      onClick={() => downloadAttachment(att)}
                      className="inline-flex items-center gap-2 px-2 py-1 rounded border border-slate-200 dark:border-slate-700 text-xs hover:bg-slate-50 dark:hover:bg-slate-800/40"
                      data-testid={`mail-att-${att.id}`}
                    >
                      <Paperclip className="h-3 w-3" />
                      <span className="font-mono truncate max-w-[200px]">{att.filename}</span>
                      <span className="text-[10px] text-slate-400">{prettyBytes(att.size)}</span>
                      <Download className="h-3 w-3 text-emerald-500" />
                    </button>
                  ))}
                </div>
              )}
            </div>
            <div className="flex-1 overflow-y-auto p-4">
              {selectedMsg.body_html ? (
                // Sandbox iframe so styles/scripts inside don't escape into the app.
                <iframe
                  title="message-body"
                  sandbox=""
                  srcDoc={selectedMsg.body_html}
                  className="w-full min-h-[400px] h-full border-0"
                />
              ) : (
                <pre className="whitespace-pre-wrap text-sm text-slate-700 dark:text-slate-200 font-sans">
                  {selectedMsg.body_text || '(içerik yok)'}
                </pre>
              )}
            </div>
          </>
        )}
      </section>
    </div>
  );
}

function prettyBytes(n) {
  if (!n && n !== 0) return '';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${Math.round(n / 102.4) / 10} KB`;
  return `${Math.round(n / 104857.6) / 10} MB`;
}
